import React from "react";
import PropTypes from "prop-types";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import FormInput from "Common/Custom/FormInput";
import FormSelect from "Common/Custom/FormSelect";
import FormNumberInput from "Common/Custom/FormNumberInput";
import FormSubmitButton from "Common/Custom/FormSubmitButton";
import Switch from "Common/Custom/Switch";

/**
 * EditLaundryProductForm — pure presentational component.
 *
 * Edit-specific field behaviour:
 *  - product_name      → select (can change product)
 *  - in_laundry_quantity → editable; max = original total_quantity
 *  - ready_quantity    → disabled; auto = original_total - in_laundry
 *  - total_quantity    → disabled; always = original total (fixed budget)
 *  - category_type     → select from static options
 *  - status            → switch
 *
 * All auto-calculation logic lives in EditLaundryProduct.jsx (container).
 * This component only renders — no useEffect, no side effects.
 */
const EditLaundryProductForm = ({
  values = {},
  errors = {},
  handleChange = () => { },
  handleSubmit = () => { },
  loading = false,
  fields = [],
  productOptions = [],
  originalTotal = 0,
}) => {
  // Fields that are always read-only (computed by the container)
  const DISABLED_FIELDS = ["ready_quantity", "total_quantity"];

  const renderField = (field) => {
    // product_name — populated from products_list
    if (field.name === "product_name" && field.type === "select") {
      return (
        <FormSelect
          key={field.name}
          field={field}
          value={values[field.name] ?? ""}
          handleChange={handleChange}
          options={productOptions}
          error={errors[field.name]}
        />
      );
    }

    if (field.type === "switch") {
      return (
        <Switch
          key={field.name}
          field={field}
          values={values[field.name]}
          handleChange={handleChange}
          error={errors[field.name]}
          ValueOne="1"
          ValueTwo="0"
          LabelOne="Active"
          LabelTwo="Inactive"
        />
      );
    }

    if (field.type === "select") {
      return (
        <FormSelect
          key={field.name}
          field={field}
          value={values[field.name] ?? ""}
          handleChange={handleChange}
          options={field.options || []}
          error={errors[field.name]}
        />
      );
    }

    if (field.type === "number") {
      const isDisabled = field.disabled || DISABLED_FIELDS.includes(field.name);

      // in_laundry_quantity — show the budget constraint in the label
      const dynamicField = field.name === "in_laundry_quantity" && originalTotal > 0
        ? { ...field, label: `Laundry Quantity (Max: ${originalTotal})` }
        : field;

      return (
        <FormInput
          key={field.name}
          field={{ ...dynamicField, disabled: isDisabled }}
          value={values[field.name] ?? 0}
          handleChange={handleChange}
          error={errors[field.name]}
          disabled={isDisabled}
        />
      );
    }

    return (
      <FormInput
        key={field.name}
        field={field}
        value={values[field.name] ?? ""}
        handleChange={handleChange}
        error={errors[field.name]}
      />
    );
  };

  return (
    <PageBody
      onSubmit={handleSubmit}
      headerchildren={<HeadingOne>Update Laundry Product</HeadingOne>}
    >
      {fields.map(renderField)}

      <FormSubmitButton
        label="Update"
        loading={loading}
        colSize="col-12"
        align="end"
      />
    </PageBody>
  );
};

EditLaundryProductForm.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object,
  handleChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  fields: PropTypes.array.isRequired,
  productOptions: PropTypes.array,
  originalTotal: PropTypes.number,
};

EditLaundryProductForm.defaultProps = {
  errors: {},
  loading: false,
  productOptions: [],
  originalTotal: 0,
};

export default EditLaundryProductForm;
